import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Icon } from "@iconify/react";

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartItems(storedCart);
  }, []);

  const removeFromCart = (productId) => {
    const updatedCart = cartItems.filter((item) => item._id !== productId);
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const increaseQuantity = (productId) => {
    const updatedCart = cartItems.map((item) =>
      item._id === productId ? { ...item, quantity: item.quantity + 1 } : item
    );
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const decreaseQuantity = (productId) => {
    const updatedCart = cartItems
      .map((item) =>
        item._id === productId && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
      .filter((item) => item.quantity > 0);
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const proceedToCheckout = () => {
    navigate("/accessories/checkout");
  };

  const totalItems = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const totalPrice = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  return (
    <div className="bg-slate-50 min-h-screen pb-24 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(99,102,241,0.04),transparent)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 relative z-10">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-widest mb-4">
            <Icon icon="solar:cart-large-4-bold-duotone" className="w-3.5 h-3.5" />
            Shopping Cart
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            Review your <span className="text-gradient">Selection</span>
          </h1>
          <p className="text-base text-gray-500 leading-relaxed max-w-xl mx-auto">
            Make sure you have all the necessary vision therapy accessories before placing your order.
          </p>
        </div>

        {cartItems.length === 0 ? (
          <div className="max-w-md mx-auto text-center py-16 bg-white border border-gray-100 rounded-3xl shadow-sm">
            <Icon icon="solar:cart-large-2-broken" className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-base font-bold text-gray-800 mb-2">Your Cart is Empty</h3>
            <p className="text-sm text-gray-500 mb-6 px-6">
              Browse our exclusive products catalog to add items and complete your therapy toolkit.
            </p>
            <Link
              to="/accessories"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm hover:bg-indigo-700 transition-colors"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          /* Cart layout split */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            
            {/* List side */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <div
                  key={item._id}
                  className="flex flex-col sm:flex-row items-center gap-4 p-5 bg-white border border-gray-100 rounded-2xl shadow-sm"
                >
                  {/* Product Thumbnail */}
                  <div className="w-20 h-20 rounded-xl bg-gray-50 overflow-hidden border border-gray-100 flex-shrink-0">
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="font-heading text-base font-bold text-gray-900 mb-1">
                      {item.name}
                    </h3>
                    <p className="text-sm font-semibold text-indigo-600">
                      ৳{item.price}
                    </p>
                  </div>

                  {/* Quantity and actions controls */}
                  <div className="flex items-center gap-6">
                    {/* Stepper */}
                    <div className="flex items-center justify-between border border-gray-200 rounded-xl px-2 py-1 bg-white">
                      <button
                        onClick={() => decreaseQuantity(item._id)}
                        className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-500 flex items-center justify-center transition-colors"
                      >
                        <Icon icon="solar:minus-circle-bold" className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center text-xs font-bold text-gray-700">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => increaseQuantity(item._id)}
                        className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-500 flex items-center justify-center transition-colors"
                      >
                        <Icon icon="solar:add-circle-bold" className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Delete button */}
                    <button
                      onClick={() => removeFromCart(item._id)}
                      className="w-9 h-9 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-500 flex items-center justify-center transition-colors"
                      title="Remove product"
                    >
                      <Icon icon="solar:trash-bin-trash-bold" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Order summary card side */}
            <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
              <h2 className="font-heading text-lg font-bold text-gray-900 mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Total Items</span>
                  <span className="font-semibold text-gray-800">{totalItems}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Price Subtotal</span>
                  <span className="font-semibold text-gray-800">৳{totalPrice}</span>
                </div>
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-gray-100 my-6" />

              <div className="flex justify-between items-baseline mb-8">
                <span className="text-sm font-bold text-gray-900">Est. Total</span>
                <span className="text-2xl font-extrabold font-heading text-indigo-600">
                  ৳{totalPrice}
                </span>
              </div>

              <button
                onClick={proceedToCheckout}
                className="w-full py-3.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-indigo-600/10"
              >
                <Icon icon="solar:card-2-bold" className="w-4 h-4" />
                Proceed to Checkout
              </button>

              <div className="mt-4 text-center">
                <Link
                  to="/accessories"
                  className="text-xs font-bold text-gray-500 hover:text-indigo-600 transition-colors uppercase tracking-wider"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
